// ============================================================
// CLUBHUB – Musterdaten Kalender (SYNTHETISCH)
// ============================================================
// Eine Vereinssaison um HEUTE herum (rund sechs Wochen davor und
// danach): Trainings als Serien, Heim- und Auswaertsspiele, Sitzungen,
// Mitgliederversammlung, Weihnachtsfeier und Arbeitseinsatz mit
// Helferschichten. Alle Tage sind relativ zum Montag der laufenden
// Woche gebaut — die Masken zeigen also immer eine lebendige Saison,
// und an einem Tag sieht jeder Aufruf dasselbe (fester Startwert je
// Termin und Vorkommen, `rng` aus `core.ts`).
//
// Personen, Gruppen und IDs stammen aus `core.ts`. Kein Gegner, Ort
// oder Gasthaus hier ist echt.
//
// Mit Sitzung treten an die Stelle dieser Datei die Abfragen in
// `lib/kalender/abfragen.ts` (ueber `lib/kalender/daten.ts`) mit
// denselben Rueckgabetypen.
// ============================================================
import { MOCK_CONTACTS, MOCK_GROUPS, MOCK_USER, pick, rng } from "./core";
import type { ContactView } from "@/lib/views";
import type { RsvpStatus } from "@/generated/prisma/enums";
import type {
  AboView,
  AntwortView,
  AnwesenheitsQuote,
  KategorieView,
  PersonKurz,
  SchichtView,
  TerminBlatt,
  TerminView,
  VorkommenView,
} from "@/lib/kalender/views";
import {
  addDays,
  addMonths,
  heuteImVerein,
  jetztImVerein,
  mondayOf,
  naivZuUtc,
  startOfMonth,
  tagVon,
  utcZuNaiv,
  weekdayIndex,
  zeitpunkt,
  type DayKey,
} from "@/lib/kalender/tag";
import { schluesselLesen, vorkommenImZeitraum, vorkommenVon, vorkommenZu } from "@/lib/kalender/vorkommen";
import { zusagenAuswerten } from "@/lib/kalender/zusagen";
import { eingeladeneAus, personKurz } from "@/lib/kalender/einladung";

// ── Kategorien ───────────────────────────────────────────────
// Farbwerte aus der Personenpalette (`lib/person-colors.ts`) — so
// stehen sie auch in `EventCategory.color`.
export const MOCK_KATEGORIEN: KategorieView[] = [
  { id: "k-training", name: "Training", color: "#10b981", sortOrder: 1 },
  { id: "k-spiel", name: "Spiel", color: "#3b82f6", sortOrder: 2 },
  { id: "k-sitzung", name: "Sitzung", color: "#8b5cf6", sortOrder: 3 },
  { id: "k-fest", name: "Fest", color: "#f59e0b", sortOrder: 4 },
  { id: "k-arbeit", name: "Arbeitseinsatz", color: "#f97316", sortOrder: 5 },
  { id: "k-treffen", name: "Treffen", color: "#78716c", sortOrder: 6 },
];

export function mockKategorie(id: string | null): KategorieView | null {
  return id ? (MOCK_KATEGORIEN.find((k) => k.id === id) ?? null) : null;
}

// ── Hilfen ───────────────────────────────────────────────────
function streuwert(text: string): number {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) >>> 0;
  return h;
}

/**
 * Wer zu einem Termin eingeladen ist — die Regel aus
 * `lib/kalender/einladung.ts` auf dem Musterbestand.
 */
export function eingeladeneVon(groupIds: string[], contactIds: string[] = []): ContactView[] {
  return eingeladeneAus(MOCK_CONTACTS, MOCK_GROUPS, groupIds, contactIds);
}

// ── Die Saison ───────────────────────────────────────────────
type Saison = { termine: TerminView[]; heute: DayKey; jetzt: string; abos: AboView[] };

const SAISON = new Map<DayKey, Saison>();

function leer(t: Partial<TerminView> & Pick<TerminView, "id" | "title" | "startsAt">): TerminView {
  return {
    description: null,
    location: null,
    endsAt: null,
    allDay: false,
    rrule: null,
    exdates: [],
    categoryId: null,
    groupIds: [],
    contactIds: [],
    visibility: "MEMBERS",
    rsvpEnabled: false,
    reminderMinutes: null,
    rsvpAllowMaybe: true,
    rsvpDeadline: null,
    maxParticipants: null,
    shifts: [],
    cancelledAt: null,
    cancelReason: null,
    ausnahmen: [],
    createdAt: `${addDays(tagVon(t.startsAt), -21)}T10:00:00`,
    ...t,
  };
}

function schichten(eventId: string, liste: [string, string, string, number, string | null][], tag: DayKey, kandidaten: ContactView[], quote: number, gaeste: string[] = []): SchichtView[] {
  const r = rng(streuwert(`${eventId}-schichten`));
  const frei = [...kandidaten];
  return liste.map(([title, von, bis, needed, note], i) => {
    const start = zeitpunkt(tag, von);
    let ende = zeitpunkt(tag, bis);
    if (ende <= start) ende = zeitpunkt(addDays(tag, 1), bis);
    const helfer: SchichtView["helfer"] = [];
    const ziel = Math.min(needed, Math.round(needed * quote + (r() - 0.5)));
    while (helfer.length < ziel && frei.length) {
      const c = frei.splice(Math.floor(r() * frei.length), 1)[0]!;
      helfer.push({ contactId: c.id });
    }
    if (gaeste[i] && helfer.length < needed) helfer.push({ name: gaeste[i]! });
    return { id: `${eventId}-s${i + 1}`, title, startsAt: start, endsAt: ende, needed, note, helfer };
  });
}

function baueSaison(heute: DayKey): Saison {
  const M = mondayOf(heute);
  const tag = (woche: number, wt: number) => addDays(M, woche * 7 + wt);
  const at = (woche: number, wt: number, hhmm: string) => zeitpunkt(tag(woche, wt), hhmm);
  const bisRegel = (d: DayKey) => `${d.replaceAll("-", "")}T235959`;
  const mitglieder = eingeladeneVon([]);
  const ah = eingeladeneVon(["g-04"]);
  const fest = eingeladeneVon(["g-09"]);
  const SPORTPLATZ = "Sportplatz Säbener Str. 51, 81547 München";
  const VEREINSHEIM = "Vereinsheim, Säbener Str. 51, 81547 München";

  // Erster Freitag zwei Monate zurueck — Beginn des Stammtischs
  let stammtisch = startOfMonth(addMonths(heute, -2));
  while (weekdayIndex(stammtisch) !== 4) stammtisch = addDays(stammtisch, 1);

  const termine: TerminView[] = [
    // ── Trainings (Serien) ──
    leer({
      id: "t-01",
      contactIds: ["c-0009"],
      reminderMinutes: 180,
      title: "Training 1. Mannschaft",
      description: "Bitte 15 Minuten vorher umgezogen auf dem Platz sein. Ab November mit Flutlicht, Hallenschuhe sind nicht nötig.",
      location: `${SPORTPLATZ}, Platz 2`,
      startsAt: at(-8, 1, "19:00"),
      endsAt: at(-8, 1, "20:30"),
      rrule: `FREQ=WEEKLY;BYDAY=TU,TH;UNTIL=${bisRegel(tag(34, 6))}`,
      exdates: [at(2, 1, "19:00"), at(2, 3, "19:00")],
      categoryId: "k-training",
      groupIds: ["g-03"],
      rsvpEnabled: true,
      rsvpDeadline: at(-8, 1, "17:00"),
      ausnahmen: [
        { occurrence: at(-1, 3, "19:00"), cancelled: true, reason: "Platz nach Starkregen gesperrt.", startsAt: null, endsAt: null },
        { occurrence: at(1, 3, "19:00"), cancelled: false, reason: "Donnerstag ist der Platz für das Jugendturnier belegt.", startsAt: at(1, 4, "18:00"), endsAt: at(1, 4, "19:30") },
      ],
      createdAt: `${tag(-10, 0)}T09:12:00`,
    }),
    leer({
      id: "t-02",
      reminderMinutes: 180,
      title: "Training Altherren",
      description: "Lockeres Training mit Abschlussspiel, danach Einkehr im Vereinsheim.",
      location: `${SPORTPLATZ}, Platz 1`,
      startsAt: at(-8, 2, "19:30"),
      endsAt: at(-8, 2, "21:00"),
      rrule: "FREQ=WEEKLY;BYDAY=WE",
      exdates: [at(2, 2, "19:30")],
      categoryId: "k-training",
      groupIds: ["g-04"],
      rsvpEnabled: true,
      ausnahmen: [{ occurrence: at(-2, 2, "19:30"), cancelled: true, reason: "Flutlicht defekt, Reparatur am Donnerstag.", startsAt: null, endsAt: null }],
    }),
    leer({
      id: "t-03",
      contactIds: ["c-0027"],
      title: "Training U13",
      description: "Eltern bitte bis spätestens 15 Uhr absagen. Trinkflasche und Schienbeinschoner mitbringen. Probetraining jederzeit möglich.",
      location: `${SPORTPLATZ}, Kleinfeld`,
      startsAt: at(-8, 0, "17:00"),
      endsAt: at(-8, 0, "18:30"),
      rrule: `FREQ=WEEKLY;BYDAY=MO,WE;UNTIL=${bisRegel(tag(8, 6))}`,
      exdates: [at(2, 0, "17:00"), at(2, 2, "17:00")],
      categoryId: "k-training",
      groupIds: ["g-06"],
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
    }),
    leer({
      id: "t-04",
      title: "Fan-Stammtisch",
      description: "Jeden ersten Freitag im Monat. Gäste willkommen.",
      location: VEREINSHEIM,
      startsAt: zeitpunkt(stammtisch, "19:30"),
      endsAt: zeitpunkt(stammtisch, "22:30"),
      rrule: "FREQ=MONTHLY;BYDAY=1FR",
      categoryId: "k-treffen",
      groupIds: ["g-08"],
      visibility: "PUBLIC",
    }),

    // ── Spiele ──
    leer({
      id: "t-05",
      reminderMinutes: 1440,
      title: "Heimspiel: Grünspitz Allstars – SV Beispielhausen",
      description: "Treffpunkt 13:45 in der Kabine. Der Kader steht nach der Frist am Freitagabend fest. Trikotsatz: grün.",
      location: `${SPORTPLATZ}, Platz 1`,
      startsAt: at(0, 6, "15:00"),
      endsAt: at(0, 6, "16:45"),
      categoryId: "k-spiel",
      groupIds: ["g-03"],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
      rsvpDeadline: at(0, 4, "20:00"),
      maxParticipants: 18,
    }),
    leer({
      id: "t-06",
      reminderMinutes: 1440,
      title: "Auswärtsspiel: FC Musterhausen – Grünspitz Allstars",
      description: "Abfahrt 12:30 am Vereinsheim. Wer fährt und Plätze frei hat, schreibt es bitte in den Kommentar.",
      location: "Sportanlage Musterhausen, Am Sportpark 3, 85999 Musterhausen",
      startsAt: at(1, 6, "14:00"),
      endsAt: at(1, 6, "15:45"),
      categoryId: "k-spiel",
      groupIds: ["g-03"],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
      rsvpDeadline: at(1, 4, "20:00"),
      maxParticipants: 18,
    }),
    leer({
      id: "t-07",
      title: "Heimspiel: Grünspitz Allstars – TSV Probstried",
      description: "Endstand 3:1. Danke an alle Helferinnen und Helfer am Grill.",
      location: `${SPORTPLATZ}, Platz 1`,
      startsAt: at(-1, 6, "15:00"),
      endsAt: at(-1, 6, "16:45"),
      categoryId: "k-spiel",
      groupIds: ["g-03"],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
      maxParticipants: 18,
    }),
    leer({
      id: "t-08",
      title: "Heimspiel: Grünspitz Allstars – SC Ahornfeld",
      location: `${SPORTPLATZ}, Platz 1`,
      startsAt: at(3, 6, "14:30"),
      endsAt: at(3, 6, "16:15"),
      categoryId: "k-spiel",
      groupIds: ["g-03"],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
      maxParticipants: 18,
    }),
    leer({
      id: "t-09",
      title: "Auswärtsspiel: DJK Lindenau – Grünspitz Allstars",
      description: "Endstand 1:1.",
      location: "Sportgelände Lindenau, Waldweg 8, 85998 Lindenau",
      startsAt: at(-3, 6, "15:00"),
      endsAt: at(-3, 6, "16:45"),
      categoryId: "k-spiel",
      groupIds: ["g-03"],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
      maxParticipants: 18,
    }),
    leer({
      id: "t-10",
      title: "Spieltag U13",
      description: "Turnier mit vier Mannschaften. Fahrgemeinschaften bitte über die Kommentare absprechen.",
      location: "Bezirkssportanlage Beispielweg 12, 81369 München",
      startsAt: at(1, 5, "10:00"),
      endsAt: at(1, 5, "13:00"),
      categoryId: "k-spiel",
      groupIds: ["g-06"],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
    }),
    leer({
      id: "t-11",
      title: "Hallenturnier Altherren",
      description: "Meldeliste für zehn Spieler. Wer zuerst zusagt, spielt — danach Warteliste.",
      location: "Dreifachhalle am Schulzentrum, Schulweg 2, 81547 München",
      startsAt: at(2, 5, "09:30"),
      endsAt: at(2, 5, "15:00"),
      categoryId: "k-spiel",
      groupIds: ["g-04"],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
      rsvpDeadline: at(1, 6, "20:00"),
      maxParticipants: 10,
    }),
    leer({
      id: "t-12",
      title: "Freundschaftsspiel Altherren gegen SG Nachbarort",
      location: `${SPORTPLATZ}, Platz 1`,
      startsAt: at(0, 5, "16:00"),
      endsAt: at(0, 5, "17:30"),
      categoryId: "k-spiel",
      groupIds: ["g-04"],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      cancelledAt: `${tag(0, 2)}T08:41:00`,
      cancelReason: "Die SG Nachbarort hat mangels Spieler abgesagt. Wir suchen einen Ersatztermin im November.",
    }),
    leer({
      id: "t-13",
      title: "Nikolausturnier Jugend",
      description: "Hallenturnier für U13 und U17 mit Kuchenverkauf. Eltern als Helfer willkommen.",
      location: "Dreifachhalle am Schulzentrum, Schulweg 2, 81547 München",
      startsAt: at(6, 5, "09:00"),
      endsAt: at(6, 5, "16:00"),
      categoryId: "k-spiel",
      groupIds: ["g-05"],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
    }),

    // ── Sitzungen und Versammlung ──
    leer({
      id: "t-14",
      contactIds: ["c-0020", "c-0022"],
      reminderMinutes: 1440,
      title: "Vorstandssitzung",
      description: "Die Kassenprüfer sind zu Punkt 1 eingeladen.\n\nTagesordnung:\n1. Kassenbericht drittes Quartal\n2. Weihnachtsfeier: Stand der Planung\n3. Beitragslauf 2027\n4. Verschiedenes",
      location: `${VEREINSHEIM}, Besprechungsraum`,
      startsAt: at(2, 2, "19:30"),
      endsAt: at(2, 2, "21:30"),
      categoryId: "k-sitzung",
      groupIds: ["g-01"],
      visibility: "BOARD",
      rsvpEnabled: true,
    }),
    leer({
      id: "t-15",
      title: "Vorstandssitzung",
      description: "Protokoll liegt in den Dokumenten (Vorstand).",
      location: `${VEREINSHEIM}, Besprechungsraum`,
      startsAt: at(-3, 2, "19:30"),
      endsAt: at(-3, 2, "21:15"),
      categoryId: "k-sitzung",
      groupIds: ["g-01"],
      visibility: "BOARD",
      rsvpEnabled: true,
    }),
    leer({
      id: "t-16",
      reminderMinutes: 2880,
      title: "Ordentliche Mitgliederversammlung 2026",
      description:
        "Einladung fristgerecht nach § 9 der Satzung.\n\nTagesordnung:\n1. Begrüßung und Feststellung der Beschlussfähigkeit\n2. Berichte des Vorstands\n3. Kassenbericht und Bericht der Kassenprüfer\n4. Entlastung des Vorstands\n5. Neuwahlen\n6. Anträge\n7. Verschiedenes\n\nAnträge bitte bis eine Woche vorher schriftlich an den Vorstand.",
      location: "Gasthaus Zur Linde, Nebenzimmer, Lindenplatz 4, 81547 München",
      startsAt: at(3, 4, "19:00"),
      endsAt: at(3, 4, "22:00"),
      categoryId: "k-sitzung",
      groupIds: [],
      visibility: "MEMBERS",
      rsvpEnabled: true,
      rsvpDeadline: at(3, 2, "23:59"),
    }),
    leer({
      id: "t-17",
      title: "Festausschuss: Planung Weihnachtsfeier",
      location: VEREINSHEIM,
      startsAt: at(0, 0, "19:00"),
      endsAt: at(0, 0, "20:30"),
      categoryId: "k-sitzung",
      groupIds: ["g-09"],
      visibility: "GROUPS",
      rsvpEnabled: true,
    }),

    // ── Feste und Arbeitseinsatz ──
    leer({
      id: "t-18",
      reminderMinutes: 2880,
      title: "Weihnachtsfeier",
      description: "Mit Tombola, Ehrungen langjähriger Mitglieder und Nikolausbesuch für die Jugend. Familien und Freunde sind herzlich willkommen — bitte für die Planung zu- oder absagen.",
      location: VEREINSHEIM,
      startsAt: at(5, 5, "18:00"),
      endsAt: at(5, 5, "23:30"),
      categoryId: "k-fest",
      groupIds: [],
      visibility: "PUBLIC",
      rsvpEnabled: true,
      rsvpDeadline: at(4, 6, "23:59"),
      maxParticipants: 90,
      shifts: schichten(
        "t-18",
        [
          ["Aufbau und Deko", "14:00", "17:30", 6, "Tische aus dem Lager, Lichterketten liegen im Schrank."],
          ["Ausschank, erste Hälfte", "18:00", "20:30", 3, null],
          ["Ausschank, zweite Hälfte", "20:30", "23:30", 3, null],
          ["Grill", "18:00", "21:00", 2, "Bratwurst und Steaks, Gasflasche ist voll."],
          ["Kuchen- und Plätzchentheke", "18:00", "21:00", 2, null],
          ["Abbau", "23:30", "01:00", 5, null],
        ],
        tag(5, 5),
        [...fest, ...mitglieder],
        0.55,
        ["", "", "Petra (Nachbarin, ohne Konto)"],
      ),
    }),
    leer({
      id: "t-19",
      title: "Herbstfest am Platz",
      description: "Danke an alle 21 Helferinnen und Helfer — Erlös für die neuen Jugendtore.",
      location: SPORTPLATZ,
      startsAt: at(-4, 5, "14:00"),
      endsAt: at(-4, 5, "22:00"),
      categoryId: "k-fest",
      visibility: "PUBLIC",
      rsvpEnabled: false,
      shifts: schichten(
        "t-19",
        [
          ["Aufbau", "10:00", "13:30", 5, null],
          ["Ausschank", "14:00", "18:00", 3, null],
          ["Ausschank", "18:00", "22:00", 3, null],
          ["Grill", "14:00", "19:00", 3, null],
          ["Abbau", "22:00", "23:30", 5, null],
        ],
        tag(-4, 5),
        mitglieder,
        1,
      ),
    }),
    leer({
      id: "t-20",
      reminderMinutes: 1440,
      title: "Arbeitseinsatz: Platz winterfest machen",
      description: "Für Brotzeit und Getränke ist gesorgt. Bitte Arbeitshandschuhe mitbringen. Zählt für die Arbeitsstunden.",
      location: SPORTPLATZ,
      startsAt: at(1, 5, "09:00"),
      endsAt: at(1, 5, "13:00"),
      categoryId: "k-arbeit",
      groupIds: [],
      rsvpEnabled: true,
      shifts: schichten(
        "t-20",
        [
          ["Laub rechen und Wege kehren", "09:00", "11:00", 6, null],
          ["Tornetze abnehmen und einlagern", "09:00", "10:30", 3, null],
          ["Dachrinne Vereinsheim reinigen", "10:00", "12:00", 2, "Leiter ist da — bitte nur schwindelfrei."],
          ["Brotzeit vorbereiten", "11:30", "13:00", 2, null],
        ],
        tag(1, 5),
        [...ah, ...mitglieder],
        0.5,
      ),
    }),

    // ── Ganztaegig und mehrtaegig ──
    leer({
      id: "t-21",
      title: "Platz gesperrt: Tiefenlockerung",
      description: "In dieser Woche entfallen alle Trainings auf dem Platz. Die Halle ist nicht verfügbar.",
      location: SPORTPLATZ,
      startsAt: `${tag(2, 0)}T00:00:00`,
      endsAt: `${tag(2, 4)}T00:00:00`,
      allDay: true,
      visibility: "MEMBERS",
    }),
    leer({
      id: "t-22",
      title: "Trainingslager U17",
      description: "Abfahrt Freitag 15 Uhr am Vereinsheim, Rückkehr Sonntag gegen 17 Uhr. Einverständniserklärung der Eltern bitte bis zur Frist abgeben.",
      location: "Jugendherberge am Beispielsee, Seestraße 1, 83999 Beispielsee",
      startsAt: `${tag(4, 4)}T00:00:00`,
      endsAt: `${tag(4, 6)}T00:00:00`,
      allDay: true,
      categoryId: "k-training",
      groupIds: ["g-07"],
      rsvpEnabled: true,
      rsvpAllowMaybe: false,
      rsvpDeadline: at(3, 4, "18:00"),
      maxParticipants: 20,
    }),
  ];

  const abos: AboView[] = [
    { id: "f-1", scope: "PERSONAL", token: "p7k2m9x4w3n8q5r6t2v9y4z3", groupId: null, label: `Persönlich (${MOCK_USER.firstName} ${MOCK_USER.lastName})`, hinweis: "Alle Termine, die dich betreffen", createdAt: `${tag(-20, 1)}T20:14:00` },
    { id: "f-2", scope: "GROUP", token: "g3h8j2k7m4n9p5q2r8s3t6u4", groupId: "g-01", label: "Vorstand", hinweis: "Sitzungen und Termine des Vorstands", createdAt: `${tag(-20, 1)}T20:15:00` },
    { id: "f-3", scope: "GROUP", token: "g9w4x2y7z3a8b5c2d6e9f3g7", groupId: "g-03", label: "1. Mannschaft", hinweis: "Training und Spiele", createdAt: `${tag(-12, 3)}T18:02:00` },
    { id: "f-4", scope: "PUBLIC", token: "oeffentlich-gruenspitz-allstars", groupId: null, label: "Öffentlicher Vereinskalender", hinweis: "Wie auf der Website", createdAt: `${tag(-30, 0)}T09:00:00` },
  ];

  return { termine, heute, jetzt: jetztImVerein(), abos };
}

/** Die Saison zum heutigen Tag (je Tag einmal gebaut). */
export function mockSaison(heute: DayKey = heuteImVerein()): Saison {
  let s = SAISON.get(heute);
  if (!s) {
    s = baueSaison(heute);
    SAISON.clear();
    SAISON.set(heute, s);
  }
  // Die Uhrzeit laeuft weiter, die Daten bleiben.
  return { ...s, jetzt: jetztImVerein() };
}

export function mockTermin(id: string): TerminView | null {
  return mockSaison().termine.find((t) => t.id === id) ?? null;
}

// ── Antworten und Anwesenheit ────────────────────────────────
const KOMMENTARE: Record<Exclude<RsvpStatus, "PENDING">, string[]> = {
  YES: [
    "Komme direkt von der Arbeit, etwa 15 Minuten später.",
    "Kann zwei Leute mitnehmen.",
    "Bringe einen Kuchen mit.",
    "Muss um 20 Uhr weg.",
    "Mit Begleitung.",
    "Bin dabei, Knie hält wieder.",
  ],
  NO: ["Bin im Urlaub.", "Krank, sorry.", "Schicht.", "Familienfeier.", "Dienstreise bis Freitag.", "Leider beruflich verhindert."],
  MAYBE: ["Entscheidet sich kurzfristig.", "Hängt von der Arbeit ab.", "Nur wenn die Kinderbetreuung klappt.", "Eher später."],
};

const ANTWORTEN = new Map<string, AntwortView[]>();

/** Antworten eines Vorkommens (bei Einzelterminen `occurrence = null`). */
export function mockAntworten(t: TerminView, occurrence: string | null, startsAt: string): AntwortView[] {
  const s = mockSaison();
  const schluessel = `${s.heute}|${t.id}|${occurrence ?? ""}`;
  const fertig = ANTWORTEN.get(schluessel);
  if (fertig) return fertig;

  const r = rng(streuwert(schluessel));
  const eingeladen = eingeladeneVon(t.groupIds, t.contactIds);
  const jetzt = naivZuUtc(s.jetzt).getTime();
  const beginn = naivZuUtc(startsAt).getTime();
  const tage = (beginn - jetzt) / 86400000;
  const vorbei = tage < 0;
  // Je naeher der Termin, desto mehr haben geantwortet.
  // Meldelisten mit Hoechstzahl fuellen sich schneller.
  const antwortQuote = Math.min(0.95, (vorbei ? 0.92 : tage < 7 ? 0.78 : tage < 21 ? 0.5 : 0.25) + (t.maxParticipants && !vorbei ? 0.3 : 0));
  const ja = t.categoryId === "k-spiel" ? 0.72 : t.categoryId === "k-sitzung" ? 0.55 : t.categoryId === "k-fest" ? 0.6 : 0.64;
  const vielleicht = t.rsvpAllowMaybe ? 0.12 : 0;

  const liste: AntwortView[] = [];
  for (const c of eingeladen) {
    const antwortet = t.rsvpEnabled && r() < antwortQuote;
    let status: RsvpStatus = "PENDING";
    let respondedAt: string | null = null;
    let comment: string | null = null;
    if (antwortet) {
      const x = r();
      status = x < ja ? "YES" : x < ja + vielleicht ? "MAYBE" : "NO";
      // Eingeladen wird drei Wochen vorher, spaetestens aber vor fuenf Tagen.
      const fruehestens = Math.min(beginn - 21 * 86400000, jetzt - 5 * 86400000);
      const spaetestens = Math.min(beginn - 3600000, jetzt - 600000);
      if (spaetestens <= fruehestens) status = "PENDING";
      else respondedAt = utcZuNaiv(new Date(fruehestens + r() * (spaetestens - fruehestens))).slice(0, 16) + ":00";
      if (status !== "PENDING" && r() < 0.22) comment = pick(r, KOMMENTARE[status]);
    }
    let attended: boolean | null = null;
    if (vorbei) {
      const p = status === "YES" ? 0.92 : status === "MAYBE" ? 0.5 : status === "NO" ? 0.03 : 0.35;
      attended = r() < p;
    }
    if (status === "PENDING" && attended === null) continue;
    liste.push({ id: `a-${t.id}-${tagVon(occurrence ?? startsAt)}-${c.id}`, contactId: c.id, occurrence, status, comment, attended, respondedAt });
  }
  ANTWORTEN.set(schluessel, liste);
  return liste;
}

function zaehlerFuer(t: TerminView, occurrence: string | null) {
  if (!t.rsvpEnabled) return null;
  const v = occurrence ?? t.startsAt;
  const ids = eingeladeneVon(t.groupIds, t.contactIds).map((c) => c.id);
  return zusagenAuswerten(mockAntworten(t, occurrence, v), ids, t.maxParticipants).zaehler;
}

/** Alle Vorkommen eines Zeitraums samt Zaehlern — fuer Monat, Woche, Liste. */
export function mockVorkommen(von: DayKey, bis: DayKey): VorkommenView[] {
  return vorkommenImZeitraum(mockSaison().termine, von, bis, { zaehler: zaehlerFuer });
}

/** Die naechsten Termine ab jetzt — fuer die Uebersicht (Startseite). */
export function naechsteTermine(anzahl = 4): VorkommenView[] {
  const s = mockSaison();
  return mockVorkommen(s.heute, addDays(s.heute, 42))
    .filter((v) => v.status !== "abgesagt" && (v.endsAt ?? v.startsAt) >= s.jetzt)
    .slice(0, anzahl);
}

export function mockAbos(): AboView[] {
  return mockSaison().abos;
}

/** Das Blatt eines Vorkommens — `null`, wenn es Termin oder Tag nicht gibt. */
export function mockTerminBlatt(key: string): TerminBlatt | null {
  const s = mockSaison();
  const { eventId, tag } = schluesselLesen(key);
  const termin = s.termine.find((t) => t.id === eventId);
  if (!termin) return null;

  // Eine Serie ohne Tag in der Adresse: das naechste Vorkommen ab heute.
  let gesucht = tag;
  if (termin.rrule && !gesucht) {
    const naechstes = vorkommenVon(termin, s.heute, addDays(s.heute, 120)).find((v) => v.status !== "abgesagt");
    gesucht = naechstes?.occurrence ? tagVon(naechstes.occurrence) : null;
  }
  const vorkommen = vorkommenZu(termin, gesucht, { zaehler: zaehlerFuer });
  if (!vorkommen) return null;

  const eingeladeneKontakte = eingeladeneVon(termin.groupIds, termin.contactIds);
  const antworten = mockAntworten(termin, vorkommen.occurrence, vorkommen.occurrence ?? vorkommen.startsAt);

  let serie: TerminBlatt["serie"] = [];
  const quoten: AnwesenheitsQuote[] = [];
  if (termin.rrule) {
    const um = tagVon(vorkommen.occurrence!);
    serie = vorkommenVon(termin, addDays(um, -56), addDays(um, 56)).map((v) => ({ key: v.key, startsAt: v.startsAt, status: v.status }));
    // Anwesenheit ueber die letzten zehn stattgefundenen Vorkommen
    const vergangen = vorkommenVon(termin, addDays(s.heute, -84), s.heute)
      .filter((v) => v.status !== "abgesagt" && v.startsAt < s.jetzt)
      .slice(-10);
    for (const c of eingeladeneKontakte) {
      let anwesend = 0;
      for (const v of vergangen) {
        const a = mockAntworten(termin, v.occurrence, v.occurrence ?? v.startsAt).find((x) => x.contactId === c.id);
        if (a?.attended) anwesend++;
      }
      quoten.push({ contactId: c.id, anwesend, von: vergangen.length });
    }
  }

  const gruppen = termin.groupIds.map((id) => ({ id, name: MOCK_GROUPS.find((g) => g.id === id)?.name ?? id }));
  const einzeln = termin.contactIds.map((id) => ({ id, name: MOCK_CONTACTS.find((c) => c.id === id)?.displayName ?? id }));

  return {
    termin,
    vorkommen,
    kategorie: mockKategorie(termin.categoryId),
    gruppen,
    einzeln,
    eingeladene: eingeladeneKontakte.map((c) => personKurz(c, termin.groupIds, s.heute)),
    antworten,
    serie,
    quoten,
    heute: s.heute,
    jetzt: s.jetzt,
  };
}

/** Personen fuer Helferlisten (Namen zu Kontakt-IDs) — ohne Mitgliedschaftspruefung. */
export function mockPersonen(ids: string[], heute: DayKey = heuteImVerein()): PersonKurz[] {
  return ids
    .map((id) => MOCK_CONTACTS.find((c) => c.id === id))
    .filter((c): c is ContactView => Boolean(c))
    .map((c) => personKurz(c, [], heute));
}
